import { useEffect, useState } from 'react';

import { isBefore, isSameDay, startOfDay, addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Absence, Employee } from 'components/types';

import { formatByLocale, fromISOString, toLocalISOString } from 'utils/date';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { Dialog } from 'components/shared';

import { ChipSelection } from './enum';
import { useDeleteAbsenceMutation } from './queries';

type Props = {
  absence: Absence;
  clickedDate: Date;
  employee: Employee;
  onClickCancel?: () => void;
  onSaveDone?: () => void;
};

const DeleteAbsenceDialog = (props: Props) => {
  const { absence, clickedDate, employee } = props;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null
  ]);

  const { mutate: deleteAbsence, isLoading } = useDeleteAbsenceMutation();

  const hasValidDateRange = () => {
    if (selectedChip && selectedChip !== ChipSelection.CUSTOM) {
      return true;
    } else if (!(dateRange[0] && dateRange[1])) {
      return false;
    } else if (isSameDay(dateRange[0], dateRange[1])) {
      return true;
    } else {
      return (
        dateRange[0] && dateRange[1] && !isBefore(dateRange[1], dateRange[0])
      );
    }
  };

  const isAbsenceDurationOneDay = isSameDay(
    fromISOString(absence.startTime),
    fromISOString(absence.endTime)
  );

  useEffect(() => {
    if (isAbsenceDurationOneDay) {
      setDateRange([
        fromISOString(absence.startTime),
        fromISOString(absence.endTime)
      ]);
    }
  }, [isAbsenceDurationOneDay]);

  const onChipClick = (name) => {
    setSelectedChip(name);

    switch (name) {
      case ChipSelection.ENTIRE_ABSENCE:
        setDateRange([
          fromISOString(absence.startTime),
          fromISOString(absence.endTime)
        ]);
        break;
      default:
        setDateRange([startOfDay(clickedDate), startOfDay(clickedDate)]);
        break;
    }
  };

  const onCancelClick = () => {
    setIsOpen(false);
    props.onClickCancel?.();
  };

  const onDeleteClick = async () => {
    let startDate, endDate;

    if (
      selectedChip === ChipSelection.ENTIRE_ABSENCE ||
      isAbsenceDurationOneDay
    ) {
      startDate = null;
      endDate = null;
    } else if (selectedChip === ChipSelection.SELECTED_DATE) {
      startDate = startOfDay(clickedDate);
      endDate = startOfDay(addDays(clickedDate, 1));
    } else {
      startDate = dateRange[0] != null ? startOfDay(dateRange[0]) : null;
      endDate =
        dateRange[1] != null ? startOfDay(addDays(dateRange[1], 1)) : null;
    }
    await deleteAbsence({
      absenceId: absence.absenceId,
      startDate: startDate ? toLocalISOString(startDate) : null,
      endDate: endDate ? toLocalISOString(endDate) : null
    });

    setIsOpen(false);
    props.onSaveDone?.();
  };

  return (
    <Dialog open={isOpen} title={t('deleteAbsenceDialog.title')}>
      <Box p={2}>
        <Typography variant="body2">
          {t('deleteAbsenceDialog.youAreAboutToDeleteForEmployee', {
            employeeName: employee.name
          })}{' '}
          {isAbsenceDurationOneDay
            ? t('deleteAbsenceDialog.thisAbsenceIsOn', {
                date: formatByLocale(fromISOString(absence.startTime))
              })
            : t('deleteAbsenceDialog.thisAbsenceIsFromUntil', {
                fromDate: formatByLocale(fromISOString(absence.startTime)),
                toDate: formatByLocale(fromISOString(absence.endTime))
              })}
          {isAbsenceDurationOneDay ? (
            <> {t('deleteAbsenceDialog.areYouSure')}</>
          ) : (
            <> {t('deleteAbsenceDialog.selectPartOfAbsenceBelow')}</>
          )}
        </Typography>

        {!isAbsenceDurationOneDay && (
          <Box mt={2} display="flex" gap={2}>
            <Chip
              variant={
                selectedChip === ChipSelection.ENTIRE_ABSENCE
                  ? 'filled'
                  : 'outlined'
              }
              color={
                selectedChip === ChipSelection.ENTIRE_ABSENCE
                  ? 'primary'
                  : undefined
              }
              onClick={() => onChipClick(ChipSelection.ENTIRE_ABSENCE)}
              label={t('deleteAbsenceDialog.entireAbsence')}
            />
            <Chip
              variant={
                selectedChip === ChipSelection.SELECTED_DATE
                  ? 'filled'
                  : 'outlined'
              }
              color={
                selectedChip === ChipSelection.SELECTED_DATE
                  ? 'primary'
                  : undefined
              }
              onClick={() => onChipClick(ChipSelection.SELECTED_DATE)}
              label={formatByLocale(clickedDate)}
            />
            <Chip
              variant={
                selectedChip === ChipSelection.CUSTOM ? 'filled' : 'outlined'
              }
              color={
                selectedChip === ChipSelection.CUSTOM ? 'primary' : undefined
              }
              onClick={() => onChipClick(ChipSelection.CUSTOM)}
              label={t('custom')}
            />
          </Box>
        )}
      </Box>
      {selectedChip === ChipSelection.CUSTOM && (
        <Box p={2} display="flex" gap={2} alignItems="end">
          <DatePicker
            value={dateRange[0]}
            minDate={fromISOString(absence.startTime)}
            maxDate={fromISOString(absence.endTime)}
            onChange={(date) => setDateRange([date, dateRange[1]])}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <Typography variant="body2">{t('until')}</Typography>
          <DatePicker
            value={dateRange[1]}
            minDate={fromISOString(absence.startTime)}
            maxDate={fromISOString(absence.endTime)}
            onChange={(date) => setDateRange([dateRange[0], date])}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          {!hasValidDateRange() && (
            <Box>
              <Typography variant="body2">
                {t('dateRangeMismatch', {
                  startDate: t('startDate'),
                  endDate: t('endDate')
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box mt={2} display="flex" gap={2} justifyContent="end">
        <Button variant="outlined" onClick={onCancelClick}>
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="primary"
          disabled={!hasValidDateRange()}
          onClick={onDeleteClick}
        >
          {t('delete')}
        </LoadingButton>
      </Box>
    </Dialog>
  );
};

export default DeleteAbsenceDialog;
